import { useState, useEffect } from 'react';
import { X, Download, Trash2, FileText, Image, File, Plus, ExternalLink } from 'lucide-react';
import api from '../../api/config';
import DocumentUploadModal from './DocumentUploadModal';

const DOC_TYPE_LABELS = {
  cotizacion: 'Cotizacion',
  orden_compra_cliente: 'OC Cliente',
  orden_compra_proveedor: 'OC Proveedor',
  guia_despacho: 'Guia Despacho',
  factura_cliente: 'Factura Cliente',
  factura_proveedor: 'Factura Proveedor',
  otro: 'Otro'
};

const DOC_TYPE_COLORS = {
  cotizacion: '#7FFFD4',
  orden_compra_cliente: '#87CEEB',
  orden_compra_proveedor: '#FFA500',
  guia_despacho: '#DDA0DD',
  factura_cliente: '#FF69B4',
  factura_proveedor: '#FFD700',
  otro: '#D3D3D3'
};

const DocumentPanel = ({ isOpen, onClose, itemNumber, itemData, onDocumentChange }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (isOpen && itemNumber) {
      loadDocuments();
    }
  }, [isOpen, itemNumber]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/documents/item/${itemNumber}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('¿Eliminar este documento?')) return;

    setDeletingId(docId);
    try {
      await api.delete(`/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      onDocumentChange?.();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error al eliminar documento');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUploadSuccess = () => {
    loadDocuments();
    onDocumentChange?.();
  };

  const getFileIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return <FileText size={20} style={{ color: '#ef4444' }} />;
    if (mimeType?.startsWith('image/')) return <Image size={20} style={{ color: '#3b82f6' }} />;
    return <File size={20} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Group documents by type
  const groupedDocs = {};
  documents.forEach(doc => {
    if (!groupedDocs[doc.doc_type]) groupedDocs[doc.doc_type] = [];
    groupedDocs[doc.doc_type].push(doc);
  });

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 9998
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', maxWidth: '95vw',
        backgroundColor: 'white', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        zIndex: 9999, display: 'flex', flexDirection: 'column',
        fontFamily: "'Space Grotesk', sans-serif"
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '2px solid #000',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'var(--theme-primary, #c4fffc)'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#000' }}>
              Documentos - Item #{itemNumber}
            </h3>
            {itemData && (
              <div style={{ fontSize: '12px', color: '#374151', marginTop: '2px' }}>
                {itemData.cliente && <span>{itemData.cliente}</span>}
                {itemData.cliente && itemData.proveedor && <span> | </span>}
                {itemData.proveedor && <span>{itemData.proveedor}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px'
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Add Button */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              width: '100%', padding: '10px',
              background: 'var(--theme-secondary, #000)',
              color: 'var(--theme-primary, #c4fffc)',
              border: '2px solid var(--theme-secondary, #000)',
              borderRadius: '8px', fontSize: '13px', fontWeight: 700,
              cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--theme-primary, #c4fffc)';
              e.currentTarget.style.color = 'var(--theme-secondary, #000)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--theme-secondary, #000)';
              e.currentTarget.style.color = 'var(--theme-primary, #c4fffc)';
            }}
          >
            <Plus size={16} />
            Subir Documento
          </button>
        </div>

        {/* Documents List */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
              Cargando documentos...
            </div>
          ) : documents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <FileText size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
              <p style={{ color: '#9ca3af', margin: 0 }}>No hay documentos adjuntos</p>
              <p style={{ color: '#d1d5db', fontSize: '12px', margin: '4px 0 0' }}>
                Haz clic en "Subir Documento" para agregar
              </p>
            </div>
          ) : (
            Object.entries(groupedDocs).map(([type, docs]) => (
              <div key={type} style={{ marginBottom: '16px' }}>
                {/* Type Header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  marginBottom: '8px', paddingBottom: '4px',
                  borderBottom: `2px solid ${DOC_TYPE_COLORS[type] || '#d1d5db'}`
                }}>
                  <div style={{
                    width: '10px', height: '10px', borderRadius: '50%',
                    background: DOC_TYPE_COLORS[type] || '#d1d5db'
                  }} />
                  <span style={{ fontWeight: 700, fontSize: '13px', color: '#374151' }}>
                    {DOC_TYPE_LABELS[type] || type}
                  </span>
                  <span style={{
                    fontSize: '11px', color: '#9ca3af',
                    background: '#f3f4f6', borderRadius: '10px', padding: '1px 8px'
                  }}>
                    {docs.length}
                  </span>
                </div>

                {/* Documents */}
                {docs.map(doc => (
                  <div
                    key={doc.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px', background: '#fafafa', borderRadius: '8px',
                      marginBottom: '6px', border: '1px solid #e5e7eb',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fafafa'; }}
                  >
                    {getFileIcon(doc.mime_type)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: '13px', color: '#1f2937',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {doc.original_name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', display: 'flex', gap: '8px' }}>
                        <span>{formatFileSize(doc.file_size)}</span>
                        {doc.doc_reference && <span>Ref: {doc.doc_reference}</span>}
                      </div>
                      <div style={{ fontSize: '10px', color: '#d1d5db', marginTop: '2px' }}>
                        {doc.uploaded_by_name} - {formatDate(doc.created_at)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Descargar / Ver"
                        style={{
                          width: '30px', height: '30px', borderRadius: '6px',
                          background: '#e0f2fe', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          textDecoration: 'none'
                        }}
                      >
                        <ExternalLink size={14} style={{ color: '#0284c7' }} />
                      </a>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        disabled={deletingId === doc.id}
                        title="Eliminar"
                        style={{
                          width: '30px', height: '30px', borderRadius: '6px',
                          background: deletingId === doc.id ? '#f3f4f6' : '#fee2e2',
                          border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <Trash2 size={14} style={{ color: deletingId === doc.id ? '#d1d5db' : '#ef4444' }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        itemNumber={itemNumber}
        itemData={itemData}
        onUploadSuccess={handleUploadSuccess}
      />
    </>
  );
};

export default DocumentPanel;
